import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'phosphor-react';
import ClickableElement from './ClickableElement';

const Methodology: React.FC = () => {
  const navigate = useNavigate();

  const goHome = () => {
    navigate('/');
  };

  return (
    <div className="relative bg-white-only dark:bg-dark-background min-h-[calc(100vh-73px)] pt-[73px]">
      {/* Back Button - fixed position relative to container */}
      <div className="fixed top-[120px] z-10 hidden md:block" style={{ left: `max(calc((100vw - 1800px) / 2 + 1rem), 1rem)` }}>
        <ClickableElement
          as="button"
          onClick={goHome}
          className="p-2 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95"
        >
          <ArrowLeft className="h-6 w-6" weight="regular" />
        </ClickableElement>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-16 relative">
        {/* Headline */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-text-primary dark:text-dark-text-primary mb-8">
            Predicting UFC Fights with Machine Learning Models
          </h1>
          <p className="text-xl text-text-secondary dark:text-dark-text-secondary">
            A custom hybrid model combining neural networks, logistic regression, and betting odds 
            for improved prediction accuracy and deeper fight analysis.
          </p>
        </div>

        {/* Methodology Content */}
        <div className="prose prose-zinc dark:prose-invert max-w-none">

          <h3>The Hybrid Model</h3>
          <p>
            Our prediction system uses a custom hybrid approach that combines multiple machine learning models 
            with optimized weights determined through rigorous testing on historical fight data. This ensemble 
            method allows us to leverage the strengths of different predictive techniques while minimizing their 
            individual weaknesses.
          </p>
          
          <div className="bg-primary/10 dark:bg-dark-primary/20 border-l-2 border-primary dark:border-dark-primary pl-4 rounded-r-lg my-6">
            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mt-3">Model Components</h4>
            <p className="mt-2 mb-3">
              The hybrid model combines three different prediction approaches with precisely calibrated weights:
            </p>
            <ul className="list-disc pl-5 mb-3">
              <li><strong>Neural Network:</strong> Deep learning model that excels at finding complex patterns</li>
              <li><strong>Logistic Regression:</strong> Statistical model that provides explainable feature importance</li>
              <li><strong>Betting Odds:</strong> Market-based signal that captures public sentiment and information</li>
            </ul>
            <p className="mb-3">
              Each weight was determined through grid search optimization on historical fight data to maximize prediction accuracy.
            </p>
          </div>
          
          <p>
            The hybrid model analyzes multiple key statistical factors for each matchup:
          </p>
          
          <ul>
            <li><strong>Striking Metrics:</strong> SLPM (Strikes Landed Per Minute), striking accuracy, and SAPM (Strikes Absorbed Per Minute)</li>
            <li><strong>Grappling Metrics:</strong> Takedown accuracy, takedown defense percentage</li>
            <li><strong>Comparative Differentials:</strong> Statistical advantages between fighters across all key metrics</li>
            <li><strong>Style Matchup Analysis:</strong> Identification of stylistic edges in striking and grappling exchanges</li>
            <li><strong>Betting Information:</strong> Odds and implied probabilities from betting markets</li>
          </ul>
          
          <p className="mb-16">
            The hybrid model consistently outperforms any individual model on historical data, with particular strength
            in identifying when betting markets might be overvaluing or undervaluing certain fighters.
          </p>


          <h3>The Prediction Process</h3>
          <p>
            When the system makes a prediction for an upcoming fight, it follows these steps:
          </p>
          
          <ol>
            <li>Retrieves the most recent fighter statistics and betting odds</li>
            <li>Feeds fighter data into each of the three model components</li>
            <li>Each model produces its own prediction and confidence score</li>
            <li>The system calculates a final prediction with overall confidence percentage</li>
          </ol>

          <div className="bg-primary/10 dark:bg-dark-primary/20 border-l-2 border-primary dark:border-dark-primary pl-4 rounded-r-lg my-6">
            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mt-3">Stylistic Matchup Analysis</h4>
            <p className="mt-2 mb-3">
              Our system goes beyond basic statistics by analyzing stylistic matchups. It can identify when:
            </p>
            <ul className="mt-2 mb-16 list-disc pl-5">
              <li>A fighter has a significant striking advantage (volume and accuracy)</li>
              <li>A fighter has superior grappling (takedown offense and defense)</li>
              <li>A fighter's defensive weaknesses match up poorly against an opponent's strengths</li>
              <li>When betting markets might be overlooking key statistical advantages</li>
            </ul>
          </div>

          <h3>Value Betting Identification</h3>
          <p>
            One of the most valuable aspects of our hybrid model is its ability to identify potential value betting opportunities:
          </p>
          
          <ul>
            <li>The system compares its predictions to betting market implied probabilities</li>
            <li>When the model strongly disagrees with the betting markets, it flags potential value bets</li>
            <li>The strongest value opportunities occur when multiple model components disagree with the betting favorite</li>
            <li>Our detailed fight analysis explains why the model believes the betting markets might be wrong</li>
          </ul>
          
          <p className="mb-16">
            While betting markets are approximately 65.8% accurate historically, our hybrid model can identify the specific fights 
            where statistical analysis suggests the market consensus might be mistaken.
          </p>

          <h3>Model Limitations and Future Improvements</h3>
          <p>
            We acknowledge several limitations in our current approach:
          </p>
          
          <ul>
            <li>MMA remains a highly unpredictable sport with inherent randomness</li>
            <li>The model cannot account for undisclosed injuries or personal factors</li>
            <li>Limited statistical data for fighters with few UFC bouts</li>
            <li>Stylistic matchups can sometimes overrule pure statistical advantages</li>
          </ul>
          
          <p className="mb-8">
            We are actively working on several improvements to our prediction system:
          </p>

          <ul>
            <li>Further feature engineering to better capture fighter style and matchup dynamics</li>
            <li>Experimenting with additional model architectures</li>
            <li>Incorporating time series components to account for fighter development</li>
            <li>Enhancing our explanation generator to provide more actionable insights</li>
          </ul>
          
          <p className="mb-8">
            We continuously work to improve our prediction system and welcome feedback from users. Our goal is to provide 
            the most accurate and insightful fight analysis possible while acknowledging the inherent unpredictability 
            that makes combat sports so exciting.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Methodology; 